/**
 * Sidebar — Hauptmenü-Navigation v7.0
 * DESIGN: Dunkle Sidebar, große komfortable Menüpunkte.
 * 
 * MENÜSTRUKTUR v7.0 (30.03.2026):
 * - Kommunikation hoch (direkt nach Dashboard)
 * - Finanzen ganz unten (über Einstellungen)
 * - Forderungen MW und Fahrzeuge nur noch unter Finanzen (nicht mehr im Hauptmenü)
 * - Rollenbasierte Sichtbarkeit: Admin sieht alles, Mitarbeiter nur Kernmodule
 * - Separator-Support für visuelle Gruppierung
 */

import { getFilteredSidebarNav, type NavChild } from '@/lib/navigation';
import { FEATURE_STATUS, type FeatureStatus, type BetriebId } from '@/lib/betriebe';
import { useAktenCounts } from '@/hooks/useSupabase';
import { useApp } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation } from 'wouter';
import { useState, useCallback, useEffect } from 'react';
import {
  LayoutDashboard, FolderOpen, CheckSquare, Calendar, Users, Receipt, Settings,
  ChevronDown, ClipboardList, BarChart3, Bell, Files, Mail, Scale, Wallet, Car,
  Target, FileSignature, Zap, KanbanSquare, ListTodo, Megaphone, Briefcase, Inbox, Wrench,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

const ICON_MAP: Record<string, LucideIcon> = {
  LayoutDashboard, FolderOpen, CheckSquare, Calendar, Users, Receipt, Settings,
  ClipboardList, BarChart3, Bell, Files, Mail, Scale, Wallet, Car,
  Target, FileSignature, Zap, KanbanSquare, ListTodo, Megaphone, Briefcase, Inbox, Wrench,
};

interface SidebarProps {
  collapsed?: boolean;
}

export default function Sidebar({ collapsed: collapsedProp }: SidebarProps) {
  const [location, setLocation] = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({
    akten: false,
    crm: false,
    finanzen: false,
    marketing: false,
    werkstatt: false,
  });
  const { data: aktenCounts } = useAktenCounts();
  const { closeAkte, setSidebarCollapsed, openForderungsmanager } = useApp();
  const { profile, impersonateUserId, teamMembers } = useAuth();

  const collapsed = collapsedProp ?? false;

  // Auto-expand: Menügruppe aufklappen wenn ein Child-Pfad aktiv ist
  useEffect(() => {
    const menuGroupPaths: Record<string, string[]> = {
      akten: ['/akten'],
      crm: ['/crm'],
      marketing: ['/marketing'],
      finanzen: ['/buchhaltung', '/rechnungen', '/datev', '/forderungen', '/fahrzeuge'],
      werkstatt: ['/werkstatt/angebote', '/werkstatt/kunden'],
    };
    const newExpanded: Record<string, boolean> = { akten: false, crm: false, finanzen: false, marketing: false, werkstatt: false };
    for (const [groupId, paths] of Object.entries(menuGroupPaths)) {
      if (paths.some(p => location === p || location.startsWith(p + '/'))) {
        newExpanded[groupId] = true;
      }
    }
    setExpandedMenus(prev => {
      // Nur updaten wenn sich etwas geändert hat, und manuell geöffnete behalten
      const merged = { ...prev };
      for (const [key, val] of Object.entries(newExpanded)) {
        if (val) merged[key] = true;
      }
      return merged;
    });
  }, [location]);

  // Navigation: Sidebar-Verhalten je nach Ziel
  const navigateTo = useCallback((path: string) => {
    closeAkte();
    setLocation(path);
    // Kommunikation: großes Menü automatisch einklappen → volle Breite für den Posteingang
    const isKommunikation = path === '/kommunikation' || path.startsWith('/kommunikation');
    // Mobile: nach jeder Navigation einklappen. Kommunikation: auf allen Geräten einklappen.
    if (isKommunikation || window.innerWidth < 768) {
      setSidebarCollapsed(true);
    }
  }, [closeAkte, setLocation, setSidebarCollapsed]);

  // Impersonate-Modus: Wenn aktiv, nutze die Permissions des Ziel-Mitarbeiters
  const impersonateTarget = impersonateUserId ? teamMembers.find(m => m.id === impersonateUserId) : null;
  const impersonateInitialen = impersonateTarget ? `${impersonateTarget.vorname?.[0] || ''}${impersonateTarget.nachname?.[0] || ''}`.toUpperCase() : null;
  const effectiveRole = impersonateTarget ? (impersonateTarget.rolle as any) : profile?.rolle;
  const effectiveModulePermissions = impersonateTarget 
    ? (impersonateTarget.module_permissions || null) 
    : profile?.modulePermissions;
  const effectivePermissions = profile?.permissions as unknown as Record<string, number | boolean> | undefined;

  // Rollenbasierte Filterung der Sidebar-Items (mit individuellen module_permissions aus DB)
  const filteredNav = getFilteredSidebarNav(
    effectiveRole,
    effectivePermissions,
    effectiveModulePermissions
  );

  const isActivePath = (path: string) => {
    if (path === '/') return location === '/' || location === '/dashboard';
    // Finanzen-Unterrouten: auch /buchhaltung/* als aktiv markieren
    if (path === '/buchhaltung') {
      return location === '/buchhaltung' || location.startsWith('/buchhaltung/');
    }
    return location === path || location.startsWith(path + '/');
  };

  const toggleMenu = (id: string) => {
    setExpandedMenus(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Gemeinsame Klassen für Menüpunkte
  const menuItemBase = `w-full flex items-center gap-3 rounded-lg mb-0.5 transition-all duration-150 cursor-pointer`;
  const menuItemExpanded = `px-3 py-2.5`;
  const menuItemCollapsed = `justify-center px-0 py-2.5`;
  const menuItemActive = `bg-[#C41E3A] text-white shadow-sm`;
  const menuItemInactive = `text-[#9CA3AF] hover:text-white hover:bg-[#374151] active:bg-[#4B5563]`;

  const renderChildItem = (child: NavChild, parentId: string) => {
    const isAktenChild = parentId === 'akten';
    const childPath = child.path || (isAktenChild ? `/akten/${child.betriebId}` : `/${parentId}/${child.id}`);
    // Für Pfade mit Query-Params (z.B. /fahrzeuge?betrieb=lcs) nur den Pfad-Teil vergleichen
    const childPathBase = childPath.split('?')[0];
    const isChildActive = location === childPathBase || location === childPath || location.startsWith(childPathBase + '/');
    const isGeplant = child.status === 'geplant';
    const count = isAktenChild ? (aktenCounts?.[child.betriebId as BetriebId] || 0) : 0;

    return (
      <button
        key={child.id}
        onClick={() => {
          if (isGeplant) {
            import('sonner').then(({ toast }) => {
              toast(`${child.label}`, { description: 'Wird in Kürze eingerichtet.' });
            });
            return;
          }
          // Forderungsmanager öffnet den globalen Drawer statt zu navigieren
          if (child.id === 'forderungsmanager') {
            openForderungsmanager();
            if (window.innerWidth < 768) setSidebarCollapsed(true);
            return;
          }
          navigateTo(childPath);
        }}
        className={`w-full flex items-center gap-2.5 rounded-lg mb-0.5 transition-all duration-150 pl-9 pr-3 py-2 cursor-pointer ${
          isGeplant ? 'opacity-40' : ''
        } ${
          isChildActive
            ? 'bg-[#374151] text-white font-semibold'
            : 'text-[#9CA3AF] hover:text-white hover:bg-[#374151] active:bg-[#4B5563]'
        }`}
      >
        <span className="text-[13px] font-medium truncate flex-1 text-left">{child.label}</span>
        {count > 0 && !isGeplant && (
          <span className="text-[10px] font-bold rounded-full min-w-[20px] h-[20px] flex items-center justify-center px-1.5 bg-[#4B5563] text-[#D1D5DB]">
            {count}
          </span>
        )}
      </button>
    );
  };

  return (
    <nav className="flex-1 overflow-y-auto sidebar-scroll py-2 px-2 flex flex-col h-full">
      {/* Dashboard — nur für Admin sichtbar */}
      {effectiveRole === 'admin' && (
        <>
          <button
            onClick={() => navigateTo('/')}
            className={`${menuItemBase} ${collapsed ? menuItemCollapsed : menuItemExpanded} ${
              isActivePath('/') ? menuItemActive : menuItemInactive
            }`}
            title={collapsed ? 'Dashboard' : undefined}
          >
            <LayoutDashboard className="w-[18px] h-[18px] flex-shrink-0" />
            {!collapsed && (
              <span className="text-[15px] font-medium leading-tight">Dashboard</span>
            )}
          </button>

          {/* Trennlinie nach Dashboard */}
          {!collapsed && <div className="h-px bg-[#374151] my-2 mx-1" />}
        </>
      )}

      {/* Sidebar Nav Items (rollenbasiert gefiltert) */}
      {filteredNav.map((item) => {
        const Icon = ICON_MAP[item.icon] || LayoutDashboard;
        const hasChildren = item.children && item.children.length > 0;
        const isItemActive = isActivePath(item.path);
        const isExpanded = expandedMenus[item.id] ?? false;

        return (
          <div key={item.id}>
            {/* Separator vor dem Item (wenn definiert) */}
            {item.separator && !collapsed && (
              <div className="h-px bg-[#374151] my-2 mx-1" />
            )}

            {/* Items mit Untermenü (CRM, Akten, Finanzen, Marketing) */}
            {hasChildren ? (
              <>
                <button
                  onClick={() => {
                    if (collapsed) {
                      const firstChild = item.children?.[0];
                      const targetPath = firstChild?.path || item.path;
                      navigateTo(targetPath);
                    } else {
                      toggleMenu(item.id);
                    }
                  }}
                  className={`${menuItemBase} ${collapsed ? menuItemCollapsed : menuItemExpanded} ${
                    isItemActive ? menuItemActive : menuItemInactive
                  }`}
                  title={collapsed ? item.label : undefined}
                >
                  <Icon className="w-[18px] h-[18px] flex-shrink-0" />
                  {!collapsed && (
                    <>
                      <div className="flex flex-col items-start flex-1 min-w-0">
                        <span className="text-[15px] font-medium leading-tight">{item.label}</span>
                      </div>
                      <ChevronDown className={`w-4 h-4 flex-shrink-0 text-gray-400 transition-transform duration-200 ${
                        isExpanded ? '' : '-rotate-90'
                      }`} />
                    </>
                  )}
                </button>

                {/* Untermenü */}
                {!collapsed && isExpanded && item.children?.map((child) =>
                  renderChildItem(child, item.id)
                )}
              </>
            ) : (
              /* Standard-Menüpunkt */
              <button
                onClick={() => navigateTo(item.path)}
                className={`${menuItemBase} ${collapsed ? menuItemCollapsed : menuItemExpanded} ${
                  isItemActive ? menuItemActive : menuItemInactive
                } relative`}
                title={collapsed ? item.label : undefined}
              >
                <div className="relative flex-shrink-0">
                  <Icon className="w-[18px] h-[18px]" />
                  {item.id === 'kommunikation' && (
                    <span className="gmail-badge absolute -top-1 -right-1.5 w-2.5 h-2.5 bg-[#C41E3A] rounded-full border border-[#1F2937] hidden" data-gmail-badge />
                  )}
                </div>
                {!collapsed && (
                  <span className="text-[15px] font-medium leading-tight flex-1 text-left">{item.label}</span>
                )}
              </button>
            )}
          </div>
        );
      })}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Bottom: User Info */}
      {!collapsed && (
        <div className="px-3 py-3 border-t border-[#374151] mt-auto">
          <div className="flex items-center gap-2.5">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${impersonateUserId ? 'bg-amber-600' : 'bg-[#C41E3A]'}`}>
              {impersonateInitialen || profile?.initialen || 'AK'}
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-xs font-semibold text-[#F3F4F6] truncate">
                {impersonateTarget ? `${impersonateTarget.vorname} ${impersonateTarget.nachname}` : (profile?.name || 'Benutzer')}
              </span>
              <span className="text-[10px] text-[#9CA3AF] truncate">
                {effectiveRole === 'admin' ? 'Administrator' : effectiveRole === 'sachbearbeiter' ? 'Sachbearbeiter' : 'Mitarbeiter'}
              </span>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
